// Preenche os modelos do escritório (procuração e declaração de hipossuficiência) com os dados do cadastro.
// Tudo acontece no navegador: os dados não saem daqui para gerar os documentos.
import {
  clip,
  concatTransformationMatrix,
  drawObject,
  endPath,
  PDFDocument,
  PDFName,
  popGraphicsState,
  pushGraphicsState,
  rectangle,
  rgb,
  StandardFonts,
  type PDFFont,
  type PDFPage,
} from 'pdf-lib';
import { formatCep, formatCpf, onlyDigits, type IntakeData } from './clientIntake';

export type SignableDocument = 'procuracao' | 'hipossuficiencia';

const TEMPLATES: Record<SignableDocument, string> = {
  procuracao: '/modelos/procuracao.pdf',
  hipossuficiencia: '/modelos/hipossuficiencia.pdf',
};
const FILE_NAMES: Record<SignableDocument, string> = {
  procuracao: 'Procuracao',
  hipossuficiencia: 'Declaracao de hipossuficiencia',
};

const INK = rgb(0.08, 0.08, 0.08);
const WHITE = rgb(1, 1, 1);
const GOLD = rgb(0.769, 0.671, 0.514);
const CITY = 'Santa Cruz do Capibaribe/PE';
const OUTORGADA_TEXT = 'brasileira, solteira, advogada, inscrita na OAB|PE n° 67.173, onde recebe notificações, intimações e outros expedientes judiciais pelo e-mail allanasthefany.adv@gmail.com e pelo telefone (81) 9.9132-2127.';
const MONTHS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

const wrap = (text: string, font: PDFFont, size: number, width: number) => {
  const lines: string[] = [];
  let line = '';
  text.split(/\s+/).forEach((word) => {
    const next = line ? `${line} ${word}` : word;
    if (font.widthOfTextAtSize(next, size) <= width || !line) line = next;
    else {
      lines.push(line);
      line = word;
    }
  });
  if (line) lines.push(line);
  return lines;
};

// Palavra no tratamento que a pessoa escolheu no cadastro: feminino, masculino ou neutro, como "portador(a)"
const gendered = (data: IntakeData, word: string) => {
  const match = word.match(/^(.*)o\(a\)$|^(.*)or\(a\)$/);
  if (!match) return word;
  if (match[2] !== undefined) {
    if (data.genero === 'feminino') return `${match[2]}ora`;
    if (data.genero === 'masculino') return `${match[2]}or`;
    return word;
  }
  if (data.genero === 'feminino') return `${match[1]}a`;
  if (data.genero === 'masculino') return `${match[1]}o`;
  return word;
};

const qualification = (data: IntakeData) => {
  // O campo do cadastro vem como "Brasileira" por padrão, e os documentos valem para qualquer pessoa
  const nationality = gendered(data, (data.nacionalidade || 'brasileiro(a)').toLowerCase().replace(/^brasileir[oa]$/, 'brasileiro(a)'));
  const civil = gendered(data, data.estado_civil.toLowerCase().replace(/^união estável$/, 'em união estável'));
  return { nationality, civil, job: data.profissao.toLowerCase(), holder: gendered(data, 'portador(a)'), resident: gendered(data, 'domiciliado(a)') };
};

const addressOf = (data: IntakeData) => [data.endereco, data.numero, data.complemento, data.bairro, `${data.cidade}/${data.uf}`].filter(Boolean).join(', ');

export const outorganteText = (data: IntakeData) => {
  const { nationality, civil, job, holder, resident } = qualification(data);
  const rg = data.rg ? ` e RG n° ${data.rg}` : '';
  return `${nationality}, ${civil}, ${job}, ${holder} do CPF n° ${formatCpf(data.cpf)}${rg}, residente e ${resident} na ${addressOf(data)}, CEP ${formatCep(data.cep)}.`;
};

export const declarationIntro = (data: IntakeData) => {
  const { nationality, civil, job, holder, resident } = qualification(data);
  const rg = data.rg ? ` e do RG nº ${data.rg}` : '';
  return `, ${nationality}, ${civil}, ${job}, ${holder} do CPF nº ${formatCpf(data.cpf)}${rg}, residente e ${resident} na ${addressOf(data)}, CEP ${formatCep(data.cep)},`;
};

// Texto justificado, como no modelo: a última linha fica normal
const drawJustified = (page: PDFPage, lines: string[], font: PDFFont, size: number, x: number, width: number, firstY: number, leading: number) => {
  lines.forEach((line, index) => {
    const y = firstY - index * leading;
    const words = line.split(' ');
    if (index === lines.length - 1 || words.length < 2) {
      page.drawText(line, { x, y, size, font, color: INK });
      return;
    }
    const used = words.reduce((sum, word) => sum + font.widthOfTextAtSize(word, size), 0);
    const gap = (width - used) / (words.length - 1);
    let cursor = x;
    words.forEach((word) => {
      page.drawText(word, { x: cursor, y, size, font, color: INK });
      cursor += font.widthOfTextAtSize(word, size) + gap;
    });
  });
};

export const isProcuracaoReady = (data: Partial<IntakeData>) =>
  !!data.nome?.trim() && !!data.genero && onlyDigits(data.cpf).length === 11 && !!data.estado_civil && !!data.profissao?.trim() &&
  onlyDigits(data.cep).length === 8 && !!data.endereco?.trim() && !!data.numero?.trim() && !!data.bairro?.trim() && !!data.cidade?.trim() && !!data.uf;

export const formattedDate = (today: Date) => `${String(today.getDate()).padStart(2, '0')} de ${MONTHS[today.getMonth()]} de ${today.getFullYear()}`;

/** Cobre um trecho e devolve a marca-d'água por cima, para não deixar remendo na página */
function eraseOverWatermark(page: PDFPage, box: { x: number; y: number; width: number; height: number }, watermark: { x: number; y: number; width: number; height: number }) {
  page.drawRectangle({ ...box, color: WHITE });
  const xobjects = page.node.Resources()!.lookup(PDFName.of('XObject')) as any;
  // A marca-d'água é a imagem com a proporção do espaço que ela ocupa na página
  const target = watermark.width / watermark.height;
  let closest: { name: string; distance: number } | null = null;
  xobjects.keys().forEach((key: PDFName) => {
    const dict = xobjects.lookup(key)?.dict;
    const width = Number(dict?.lookup(PDFName.of('Width'))?.toString() || 0);
    const height = Number(dict?.lookup(PDFName.of('Height'))?.toString() || 0);
    if (!width || !height) return;
    const distance = Math.abs(width / height - target);
    if (!closest || distance < closest.distance) closest = { name: key.toString().replace(/^\//, ''), distance };
  });
  page.pushOperators(
    pushGraphicsState(),
    rectangle(box.x, box.y, box.width, box.height),
    clip(),
    endPath(),
    concatTransformationMatrix(watermark.width, 0, 0, watermark.height, watermark.x, watermark.y),
    drawObject(closest!.name),
    popGraphicsState(),
  );
}

async function fillProcuracao(pdf: PDFDocument, data: IntakeData, today: Date) {
  const page = pdf.getPage(0);
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);

  // Quadro da outorgante: o nome fica na borda, como no modelo
  page.drawRectangle({ x: 78, y: 566, width: 426, height: 104, color: WHITE });
  const nameSize = bold.widthOfTextAtSize(data.nome, 10.5) > 380 ? 8.5 : 10.5;
  const nameEnd = 100 + bold.widthOfTextAtSize(data.nome, nameSize);
  page.drawText(data.nome, { x: 100, y: 658, size: nameSize, font: bold, color: INK });
  const border = { thickness: 0.75, color: GOLD };
  page.drawLine({ start: { x: 81, y: 662 }, end: { x: 95, y: 662 }, ...border });
  page.drawLine({ start: { x: Math.min(nameEnd + 8, 495), y: 662 }, end: { x: 501, y: 662 }, ...border });

  const text = outorganteText(data);
  let size = 10.5;
  let lines = wrap(text, font, size, 390);
  while (lines.length * (size + 5) > 78 && size > 8) {
    size -= 0.5;
    lines = wrap(text, font, size, 390);
  }
  drawJustified(page, lines, font, size, 96, 390, 642, size + 5);
  // O quadro termina logo abaixo do texto
  const clientBottom = Math.max(570, 642 - (lines.length - 1) * (size + 5) - 11);
  page.drawLine({ start: { x: 81, y: 662 }, end: { x: 81, y: clientBottom }, ...border });
  page.drawLine({ start: { x: 501, y: 662 }, end: { x: 501, y: clientBottom }, ...border });
  page.drawLine({ start: { x: 81, y: clientBottom }, end: { x: 501, y: clientBottom }, ...border });

  // Quadro da outorgada: o nome já está no modelo; o texto é escrito aqui para sair nítido
  page.drawLine({ start: { x: 81, y: 532 }, end: { x: 94, y: 532 }, ...border });
  page.drawLine({ start: { x: 268, y: 532 }, end: { x: 501, y: 532 }, ...border });
  const lawyerLines = wrap(OUTORGADA_TEXT, font, 10.5, 390);
  drawJustified(page, lawyerLines, font, 10.5, 96, 390, 512, 15.5);
  const lawyerBottom = 512 - (lawyerLines.length - 1) * 15.5 - 11;
  page.drawLine({ start: { x: 81, y: 532 }, end: { x: 81, y: lawyerBottom }, ...border });
  page.drawLine({ start: { x: 501, y: 532 }, end: { x: 501, y: lawyerBottom }, ...border });
  page.drawLine({ start: { x: 81, y: lawyerBottom }, end: { x: 501, y: lawyerBottom }, ...border });

  // Data sobre a marca-d'água
  eraseOverWatermark(page, { x: 240, y: 141, width: 288, height: 18 }, { x: 240, y: 110, width: 396, height: 557 });
  const date = `${CITY}, ${formattedDate(today)}.`;
  page.drawText(date, { x: 523.5 - font.widthOfTextAtSize(date, 10.5), y: 146, size: 10.5, font, color: INK });

  // Nome abaixo da linha de assinatura
  page.drawRectangle({ x: 100, y: 58, width: 396, height: 18, color: WHITE });
  const signSize = bold.widthOfTextAtSize(data.nome, 10.5) > 390 ? 8.5 : 10.5;
  page.drawText(data.nome, { x: 297.8 - bold.widthOfTextAtSize(data.nome, signSize) / 2, y: 63, size: signSize, font: bold, color: INK });
}

async function fillDeclaracao(pdf: PDFDocument, data: IntakeData, today: Date) {
  const page = pdf.getPage(0);
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
  const LEFT = 72;
  const RIGHT = 522.5;
  const WATERMARK = { x: 280, y: 175, width: 306, height: 385 };

  // O parágrafo inteiro é reescrito para fluir com qualquer tamanho de nome e endereço
  eraseOverWatermark(page, { x: 66, y: 481, width: 464, height: 206 }, WATERMARK);
  const plain = (text: string) => text.split(/\s+/).map((part) => ({ text: part, font, color: INK }));
  const nameWords = data.nome.split(/\s+/).map((part) => ({ text: part, font: bold, color: INK }));
  nameWords[nameWords.length - 1].text += ',';
  const words = [
    ...nameWords,
    ...plain(declarationIntro(data).replace(/^,\s*/, '')),
    { text: 'DECLARO,', font: bold, color: GOLD },
    ...plain('para todos os fins de direito e sob as penas da lei, que não tenho condições de arcar com as despesas inerentes ao presente processo sem prejuízo do meu sustento e de minha família, a fim de pleitear a concessão da'),
    { text: 'JUSTIÇA', font: bold, color: GOLD },
    { text: 'GRATUITA,', font: bold, color: GOLD },
    ...plain('nos termos do inciso LXXIV do artigo 5º da Constituição Federal, combinado com a Lei nº 1.060/50, artigo 1º da Lei nº 7.115/83, e nos termos do artigo 98 e seguintes da Lei nº 13.105/2015, Código de Processo Civil.'),
  ];

  const layout = (fontSize: number) => {
    const lines: (typeof words)[] = [[]];
    const space = font.widthOfTextAtSize(' ', fontSize);
    let width = 36;
    words.forEach((word) => {
      const w = word.font.widthOfTextAtSize(word.text, fontSize);
      if (lines[lines.length - 1].length && width + space + w > RIGHT - LEFT) {
        lines.push([]);
        width = 0;
      }
      width += (lines[lines.length - 1].length ? space : 0) + w;
      lines[lines.length - 1].push(word);
    });
    return lines;
  };
  let size = 11;
  let leading = 20;
  let lines = layout(size);
  while (lines.length * leading > 192 && size > 8.5) {
    size -= 0.5;
    leading = size + 8;
    lines = layout(size);
  }
  // Termina no mesmo ponto do modelo, perto de "Requeiro": linhas a mais sobem em direção ao título
  lines.forEach((line, index) => {
    const y = 487 + (lines.length - 1 - index) * leading;
    const start = LEFT + (index === 0 ? 36 : 0);
    const used = line.reduce((sum, word) => sum + word.font.widthOfTextAtSize(word.text, size), 0);
    const isLast = index === lines.length - 1;
    const gap = !isLast && line.length > 1 ? (RIGHT - start - used) / (line.length - 1) : font.widthOfTextAtSize(' ', size);
    let x = start;
    line.forEach((word) => {
      page.drawText(word.text, { x, y, size, font: word.font, color: word.color });
      x += word.font.widthOfTextAtSize(word.text, size) + gap;
    });
  });

  // Data e nome sobre a marca-d'água
  eraseOverWatermark(page, { x: 258, y: 397, width: 270, height: 18 }, WATERMARK);
  const date = `${CITY}, ${formattedDate(today)}.`;
  page.drawText(date, { x: RIGHT - font.widthOfTextAtSize(date, 11), y: 402, size: 11, font, color: INK });

  eraseOverWatermark(page, { x: 70, y: 296, width: 456, height: 18 }, WATERMARK);
  const signSize = bold.widthOfTextAtSize(data.nome, 11) > 440 ? 8.5 : 11;
  page.drawText(data.nome, { x: 297.8 - bold.widthOfTextAtSize(data.nome, signSize) / 2, y: 302, size: signSize, font: bold, color: INK });
}

export async function buildDocumentPdf(kind: SignableDocument, data: IntakeData, template: ArrayBuffer | Uint8Array, today = new Date()) {
  const pdf = await PDFDocument.load(template);
  if (kind === 'procuracao') await fillProcuracao(pdf, data, today);
  else await fillDeclaracao(pdf, data, today);
  pdf.setTitle(`${kind === 'procuracao' ? 'Procuração' : 'Declaração de hipossuficiência'} - ${data.nome}`);
  pdf.setAuthor('Allana Sthéfany Advocacia');
  return pdf.save();
}

export async function downloadDocument(kind: SignableDocument, data: IntakeData) {
  const response = await fetch(TEMPLATES[kind]);
  if (!response.ok) throw new Error('Não foi possível abrir o modelo do documento.');
  const bytes = await buildDocumentPdf(kind, data, await response.arrayBuffer());
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `${FILE_NAMES[kind]} - ${data.nome}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}
